import React, { useState } from 'react';
import axios from 'axios';
import constant from '../constants/constant';
import { 
  ShieldAlert, 
  Trash2, 
  Lock, 
  Eye, 
  EyeOff, 
  CheckCircle2, 
  AlertTriangle, 
  Database, 
  RefreshCw,
  Layers,
  Users,
  Award
} from 'lucide-react';

const SecretSystemClean = () => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleExecuteClean = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);

    if (!password) {
      setError('Please enter the secret master authorization password.');
      return;
    }

    if (password !== 'raza@123') {
      setError('Invalid Secret Password! Unauthorized access.');
      return;
    }

    if (!isAgreed) {
      setError('Please confirm the agreement checkbox to proceed.');
      return;
    }

    if (!window.confirm('⚠️ CRITICAL WARNING ⚠️\n\nAre you 100% sure you want to clean the system for fresh testing?\nThis will erase all test users, transactions, gifts, and room data while keeping Admin accounts, Levels, and Store intact.')) {
      return;
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem('adminToken');
      const res = await axios.post(
        `${constant.API_BASE_URL}/admin/business/clean-system-testing`,
        { secret_password: password },
        { headers: { Authorization: token } }
      );

      if (res.data.success) {
        setResult(res.data);
        setPassword('');
        setIsAgreed(false);
      } else {
        setError(res.data.message || 'Operation failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error executing clean system workflow');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6 md:p-12 flex flex-col items-center justify-center">
      <div className="w-full max-w-3xl space-y-8 animate-in fade-in zoom-in-95 duration-500">
        
        {/* Header Danger Zone */}
        <div className="bg-gradient-to-r from-red-950/80 via-slate-900 to-red-950/80 border border-red-500/30 rounded-3xl p-8 shadow-2xl shadow-red-950/50 relative overflow-hidden">
          <div className="absolute -right-8 -bottom-8 opacity-10 text-red-500">
            <Trash2 size={240} />
          </div>

          <div className="relative z-10 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-500/20 text-red-400 border border-red-500/30 rounded-full text-xs font-black uppercase tracking-wider">
              <ShieldAlert size={14} /> Secret Maintenance Console
            </div>
            
            <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight flex items-center gap-3">
              Fresh Testing System Reset
            </h1>
            
            <p className="text-slate-300 text-sm md:text-base leading-relaxed">
              This secret tool executes <code className="bg-black/60 px-2 py-1 rounded text-red-300 font-mono text-xs">clean_for_fresh_testing.js</code>. 
              It wipes all dynamic test data, rooms, gifts, wallets, and non-admin users so you can test entire target flows completely fresh.
            </p>
          </div>
        </div>

        {/* Data Scope Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-slate-900/90 border border-red-500/20 rounded-2xl p-5 space-y-3">
            <div className="flex items-center gap-2 text-red-400 font-bold text-sm">
              <AlertTriangle size={18} /> Will Be Cleaned / Reset:
            </div>
            <ul className="text-xs text-slate-400 space-y-1.5 list-disc list-inside">
              <li>All Non-Admin Users (<code className="text-red-300">public.user</code>)</li>
              <li>Live Streams, Rooms, Sessions & Bans</li>
              <li>Gift Records, PK Battles, Lucky Bags</li>
              <li>Agency Applications, Transfers & Hierarchy</li>
              <li>Host Live Sessions & Target Progress</li>
              <li>Pending Rewards & Pending Commissions</li>
              <li>User Wallets & Transaction Histories</li>
            </ul>
          </div>

          <div className="bg-slate-900/90 border border-emerald-500/20 rounded-2xl p-5 space-y-3">
            <div className="flex items-center gap-2 text-emerald-400 font-bold text-sm">
              <CheckCircle2 size={18} /> 100% Preserved & Intact:
            </div>
            <ul className="text-xs text-slate-400 space-y-1.5 list-disc list-inside">
              <li>Super Admin & Admin User Accounts</li>
              <li>System Roles & Permissions Structure</li>
              <li>Target Levels (<code className="text-emerald-300">main.target_levels</code>)</li>
              <li>Store Items, Badges & Gifts Definition</li>
              <li>System Settings, Maintenance & News</li>
              <li>Company Configuration (<code className="text-emerald-300">public.company</code>)</li>
            </ul>
          </div>
        </div>

        {/* Execution Form */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 shadow-xl space-y-6">
          <form onSubmit={handleExecuteClean} className="space-y-6">
            
            {/* Password Input */}
            <div className="space-y-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                Secret Master Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500">
                  <Lock size={18} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter secret authorization password"
                  className="w-full bg-slate-950 border border-slate-800 focus:border-red-500 rounded-xl pl-11 pr-12 py-3.5 text-white font-mono text-sm focus:outline-none transition-all placeholder:text-slate-600"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-500 hover:text-slate-300 transition"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Confirmation Checkbox */}
            <div className="flex items-start gap-3 bg-slate-950/60 p-4 rounded-xl border border-slate-800">
              <input
                type="checkbox"
                id="agree"
                checked={isAgreed}
                onChange={(e) => setIsAgreed(e.target.checked)}
                className="mt-1 w-4 h-4 rounded bg-slate-900 border-slate-700 text-red-600 focus:ring-red-500 cursor-pointer"
              />
              <label htmlFor="agree" className="text-xs text-slate-400 cursor-pointer select-none">
                I understand this operation will permanently delete all test users and transactions, leaving only Admin accounts and configuration levels for fresh testing.
              </label>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-950/50 border border-red-500/40 rounded-xl text-red-400 text-sm flex items-center gap-2">
                <AlertTriangle size={18} className="shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Submit Action */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-red-600 to-rose-700 hover:from-red-500 hover:to-rose-600 text-white font-extrabold rounded-xl shadow-lg shadow-red-600/30 transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {loading ? (
                <>
                  <RefreshCw className="animate-spin" size={20} />
                  <span>Executing Fresh Clean Workflow...</span>
                </>
              ) : (
                <>
                  <Trash2 size={20} />
                  <span>Execute System Reset For Fresh Testing</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Success Output Report */}
        {result && (
          <div className="bg-emerald-950/40 border border-emerald-500/40 rounded-3xl p-8 shadow-2xl space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex items-center gap-3 text-emerald-400 font-extrabold text-lg">
              <CheckCircle2 size={24} />
              <span>{result.message}</span>
            </div>

            <div className="bg-slate-950 p-4 rounded-2xl border border-emerald-500/20 text-xs font-mono space-y-2">
              <p className="text-emerald-300 font-bold">
                ✓ Non-Admin Users Deleted: {result.deleted_users_count}
              </p>
              <p className="text-slate-400">
                ✓ Client Transactional Tables Cleaned: 35+ Tables
              </p>
              <p className="text-slate-400">
                ✓ User Wallets Reset to 0
              </p>
              <div className="pt-2 border-t border-slate-800 space-y-1">
                <p className="text-slate-400 font-sans font-bold">Preserved System Configs:</p>
                {result.preserved?.map((p, idx) => (
                  <p key={idx} className="text-slate-500">  • {p}</p>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default SecretSystemClean;
